<script setup>
import { computed, ref, shallowRef, watch } from 'vue';
import { useRoute, useRouter } from 'vue-router';
import UiBadge from '@/components/ui/UiBadge.vue';
import UiFormField from '@/components/ui/UiFormField.vue';
import RoleMenuEditor from './RoleMenuEditor.vue';
import { countPages, parseMenu } from './menu';
import { apiRequest, assertOk, errorMessage } from '@/api/http';
import { useConfirm } from '@/composables/useConfirm';
import { useListResource } from '@/composables/useListResource';
import { useToast } from '@/composables/useToast';
import { required } from '@/utils/validation';

// Roles on the left, the selected role's sidebar menu on the right, edited
// in place (no modal). The selection is kept in ?role= for reload/links.
const BUILT_IN = [1, 2, 4, 21, 22]; // referenced by id in the code
const SUPERADMIN_ROLE_ID = 4;

const route = useRoute();
const router = useRouter();
const { confirm } = useConfirm();
const toast = useToast();

const search = shallowRef('');
const { items: roles, loading, error, load } = useListResource(() =>
  apiRequest('get', '/assigned-modules'),
);
const pages = shallowRef([]);
apiRequest('get', '/sys-modules')
  .then((rows) => (pages.value = Array.isArray(rows) ? rows : []))
  .catch(() => {});

const listed = computed(() => {
  const q = search.value.trim().toLowerCase();
  return roles.value
    .map((r) => ({
      ...r,
      pageCount: countPages(parseMenu(r.assign_mods)),
      builtIn: BUILT_IN.includes(Number(r.id)),
    }))
    .filter((r) => !q || r.description.toLowerCase().includes(q));
});

// --- selection + draft --------------------------------------------------
const selectedId = computed(() => route.query.role ?? null); // id or 'new'
const selected = computed(() =>
  selectedId.value === 'new'
    ? null
    : roles.value.find((r) => String(r.id) === String(selectedId.value)) ?? null,
);
const isNew = computed(() => selectedId.value === 'new');

const formRef = ref(null);
const name = shallowRef('');
const menu = ref([]);
const original = shallowRef('');
const saving = shallowRef(false);

const snapshot = () => JSON.stringify({ name: name.value.trim(), menu: menu.value });
// Start in sync with the empty draft, so nothing counts as unsaved yet.
original.value = snapshot();
const dirty = computed(() => snapshot() !== original.value);

function loadDraft() {
  name.value = selected.value?.description ?? '';
  menu.value = selected.value ? parseMenu(selected.value.assign_mods) : [];
  original.value = snapshot();
  formRef.value?.resetValidation();
}
watch([selected, isNew], loadDraft);

function setSelection(id) {
  router.replace({ query: { ...route.query, role: id == null ? undefined : String(id) } });
}

// User-initiated switch: ask before throwing away unsaved edits.
async function select(id) {
  if (String(id) === String(selectedId.value)) return;
  if (dirty.value) {
    const ok = await confirm({
      title: 'Discard changes?',
      description: 'You have unsaved changes to this role.',
      confirmText: 'Discard',
      destructive: true,
    });
    if (!ok) return;
  }
  setSelection(id);
}

// Pick the first role once the list is loaded (if nothing is selected).
load().then(() => {
  if (!selectedId.value && roles.value.length) setSelection(roles.value[0].id);
  else loadDraft();
});

// --- rules ------------------------------------------------------------------
const locksOut = computed(
  () =>
    Number(selected.value?.id) === SUPERADMIN_ROLE_ID &&
    !menu.value.some(
      (i) => i.route === '/user-modules' || (i.subLink || []).some((s) => s.route === '/user-modules'),
    ),
);
const canDelete = computed(
  () => selected.value && !BUILT_IN.includes(Number(selected.value.id)),
);

// --- actions ------------------------------------------------------------
async function save() {
  const { valid } = await formRef.value.validate();
  if (!valid || locksOut.value) return;
  saving.value = true;
  try {
    const data = { description: name.value.trim(), assign_mods: JSON.stringify(menu.value) };
    assertOk(
      selected.value
        ? await apiRequest('patch', `/assigned-modules/${selected.value.id}`, data)
        : await apiRequest('post', '/assigned-modules', data),
    );
    toast.success(isNew.value ? 'Role created.' : 'Changes saved.');
    const createdName = data.description;
    original.value = snapshot();
    await load();
    if (isNew.value) {
      const created = roles.value.find((r) => r.description === createdName);
      if (created) setSelection(created.id);
    } else {
      loadDraft();
    }
  } catch (e) {
    toast.error(errorMessage(e, 'Could not save the role.'));
  } finally {
    saving.value = false;
  }
}

async function remove() {
  const role = selected.value;
  const ok = await confirm({
    title: 'Delete role?',
    description: `${role.description} will be removed. Roles still assigned to users can't be deleted.`,
    confirmText: 'Delete',
    destructive: true,
  });
  if (!ok) return;
  try {
    assertOk(await apiRequest('delete', `/assigned-modules/${role.id}`));
    toast.success('Role deleted.');
    original.value = snapshot(); // nothing left to discard
    await load();
    setSelection(roles.value[0]?.id ?? null);
  } catch (e) {
    toast.error(errorMessage(e, 'Could not delete the role.'));
  }
}
</script>

<template>
  <div class="roles">
    <!-- Role list -->
    <aside class="roles__list" aria-label="Roles">
      <div class="roles__list-head">
        <v-text-field
          v-model="search"
          placeholder="Search roles"
          prepend-inner-icon="mdi-magnify"
          density="compact"
          hide-details
          clearable
        />
        <v-btn
          icon="mdi-plus"
          variant="outlined"
          size="small"
          aria-label="New role"
          title="New role"
          @click="select('new')"
        />
      </div>

      <v-alert v-if="error" type="error" variant="tonal" density="compact">{{ error }}</v-alert>
      <div v-else-if="loading && !roles.length" class="d-flex justify-center pa-6">
        <v-progress-circular indeterminate size="24" width="2" />
      </div>

      <nav class="roles__items">
        <button
          v-if="isNew"
          type="button"
          class="roles__item roles__item--active"
          aria-current="true"
        >
          <span class="roles__item-name">New role</span>
          <span class="roles__item-meta">Not saved</span>
        </button>
        <button
          v-for="r in listed"
          :key="r.id"
          type="button"
          class="roles__item"
          :class="{ 'roles__item--active': String(r.id) === String(selectedId) }"
          :aria-current="String(r.id) === String(selectedId) ? 'true' : undefined"
          @click="select(r.id)"
        >
          <span class="roles__item-name">
            {{ r.description }}
            <UiBadge v-if="r.builtIn" variant="outline">Built-in</UiBadge>
          </span>
          <span class="roles__item-meta">{{ r.pageCount }} page{{ r.pageCount === 1 ? '' : 's' }}</span>
        </button>
        <p v-if="!loading && !listed.length && !isNew" class="roles__none">No roles match.</p>
      </nav>
    </aside>

    <!-- Editor -->
    <section v-if="selected || isNew" class="roles__editor">
      <header class="roles__editor-head">
        <div>
          <h3 class="roles__editor-title">{{ isNew ? 'New role' : selected.description }}</h3>
          <p class="roles__editor-sub">
            {{ countPages(menu) }} page{{ countPages(menu) === 1 ? '' : 's' }} in the sidebar
          </p>
        </div>
        <v-btn
          v-if="canDelete"
          variant="outlined"
          color="error"
          size="small"
          prepend-icon="mdi-trash-can-outline"
          @click="remove"
        >
          Delete role
        </v-btn>
      </header>

      <v-form ref="formRef" class="form-stack" @submit.prevent="save">
        <UiFormField label="Role name" required v-slot="{ id }">
          <v-text-field :id="id" v-model="name" :rules="[required]" hide-details="auto" />
        </UiFormField>

        <UiFormField
          label="Sidebar menu"
          description="In the order users will see it. Drag ⋮⋮ to reorder; groups hold sub-pages."
        >
          <RoleMenuEditor v-model="menu" :pages="pages" />
        </UiFormField>

        <v-alert v-if="locksOut" type="warning" variant="tonal" density="compact">
          Keep <strong>User Modules</strong> in the System Owner menu, or nobody will be able to manage roles.
        </v-alert>
      </v-form>

      <footer class="roles__editor-actions">
        <v-btn
          color="primary"
          :loading="saving"
          :disabled="!dirty || locksOut"
          @click="save"
        >
          {{ isNew ? 'Create role' : 'Save changes' }}
        </v-btn>
        <v-btn v-if="dirty && !saving" variant="text" @click="loadDraft">Discard</v-btn>
      </footer>
    </section>

    <section v-else-if="!loading" class="roles__editor roles__editor--empty">
      Select a role to edit its sidebar menu.
    </section>
  </div>
</template>

<style scoped>
.roles {
  display: grid;
  grid-template-columns: 280px minmax(0, 1fr);
  gap: 20px;
  align-items: start;
}

.roles__list,
.roles__editor {
  background: var(--card);
  border: 1px solid var(--border);
  border-radius: 14px;
  box-shadow: 0 1px 3px 0 rgba(0, 0, 0, 0.1), 0 1px 2px -1px rgba(0, 0, 0, 0.1);
}

.roles__list {
  display: flex;
  flex-direction: column;
  gap: 12px;
  padding: 12px;
  position: sticky;
  top: 80px;
}
.roles__list-head {
  display: flex;
  gap: 8px;
  align-items: center;
}
.roles__items {
  display: flex;
  flex-direction: column;
  gap: 2px;
}
.roles__item {
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 2px;
  width: 100%;
  padding: 8px 10px;
  border: 0;
  border-radius: 8px;
  background: transparent;
  font: inherit;
  text-align: left;
  cursor: pointer;
}
.roles__item:hover {
  background: var(--accent);
}
.roles__item--active {
  background: var(--brand-soft);
}
.roles__item--active .roles__item-name {
  color: var(--brand);
}
.roles__item:focus-visible {
  outline: none;
  box-shadow: 0 0 0 3px rgba(var(--brand-rgb), 0.3);
}
.roles__item-name {
  display: flex;
  align-items: center;
  gap: 6px;
  font-size: 14px;
  font-weight: 500;
  color: var(--foreground);
}
.roles__item-meta {
  font-size: 12px;
  color: var(--muted-foreground);
}
.roles__none {
  margin: 0;
  padding: 12px;
  font-size: 14px;
  color: var(--muted-foreground);
}

.roles__editor {
  display: flex;
  flex-direction: column;
  gap: 24px;
  padding: 24px;
}
.roles__editor--empty {
  align-items: center;
  justify-content: center;
  min-height: 200px;
  color: var(--muted-foreground);
  font-size: 14px;
}
.roles__editor-head {
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 12px;
}
.roles__editor-title {
  margin: 0;
  font-size: 18px;
  font-weight: 600;
}
.roles__editor-sub {
  margin: 4px 0 0;
  font-size: 14px;
  color: var(--muted-foreground);
}
.roles__editor-actions {
  display: flex;
  gap: 8px;
  padding-top: 16px;
  border-top: 1px solid var(--border);
}

@media (max-width: 960px) {
  .roles {
    grid-template-columns: 1fr;
  }
  .roles__list {
    position: static;
  }
}
</style>
